import { Box, Button, CircularProgress, type ButtonProps } from "@mui/material";
import { visuallyHiddenSx } from "@gui/widgets/A11y";

interface BusyButtonProps extends ButtonProps {
	/**
	 * The action is in flight: the icon becomes a spinner (the end icon when the button has only
	 * an end icon, the start icon otherwise), the button is marked `aria-busy`, and
	 * clicks are ignored while it keeps focus (`aria-disabled`, not `disabled`, which would drop
	 * focus to the page the moment it was pressed). React commits after every click, so a double
	 * click cannot reach the handler twice; a handler with another way in (Enter in a form)
	 * needs its own guard.
	 */
	busy?: boolean;
	/**
	 * Announced once, politely, when the action starts: "Downloading logs". Keep it fixed; put a
	 * countdown or progress in the visible label, which is not re-announced.
	 */
	busyLabel?: string;
}

/** Spinner sizes that match MUI's start-icon sizes, so the label does not shift. */
const SPINNER_PX = { small: 16, medium: 18, large: 20 } as const;

/**
 * A button for an action that takes a while. Both page toolbars draw their actions with it; use it
 * for a long-running submit in a page body too (docs/design-system.md §4).
 */
export function BusyButton(props: BusyButtonProps) {
	const { busy = false, busyLabel, startIcon, endIcon, onClick, disabled, ...rest } = props;
	const spinner = <CircularProgress size={SPINNER_PX[rest.size ?? "medium"]} color="inherit" />;
	// The spinner takes the icon's place: a trailing icon (Submit's ▾) when that is the only one.
	const spinsAtEnd = endIcon !== undefined && startIcon === undefined;
	return (
		<>
			<Button
				{...rest}
				startIcon={busy && !spinsAtEnd ? spinner : startIcon}
				endIcon={busy && spinsAtEnd ? spinner : endIcon}
				onClick={busy ? undefined : onClick}
				disabled={!busy && disabled}
				aria-disabled={busy || undefined}
				aria-busy={busy || undefined}
			/>
			{/* A live region is only read when its content changes, so it stays mounted for as long as
			    the action could become busy and is filled when it does. */}
			{busyLabel !== undefined && (
				<Box component="span" role="status" sx={visuallyHiddenSx}>
					{busy ? busyLabel : ""}
				</Box>
			)}
		</>
	);
}
